import { type JSX, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import { errorMessage } from "@/api";

/**
 * The three things every async surface in the app has to be able to say: this
 * is loading, there is nothing here, this failed. One module so a list, a chart
 * and a dialog answer those questions in the same shape — a reader who learns
 * the dashed border means "nothing yet" on one page knows it everywhere.
 *
 * Loading is always a skeleton of the content to come, never a spinner: a
 * spinner that flashes for 80ms reads as a glitch, and one that spins for three
 * seconds says nothing about what is arriving.
 */

export interface LoadingStateProps {
  /** Accessible name, e.g. "Loading sessions". */
  readonly label?: string;
  /** Skeleton bars to draw; match it roughly to the content it stands in for. */
  readonly rows?: number;
  readonly className?: string;
}

export function LoadingState({
  label = "Loading",
  rows = 3,
  className,
}: LoadingStateProps): JSX.Element {
  const widths = ["w-2/5", "w-4/5", "w-3/5", "w-3/4", "w-1/2"];
  return (
    <div
      aria-label={label}
      className={cn("grid min-h-24 content-center gap-2 border border-dashed p-4", className)}
      role="status"
    >
      {Array.from({ length: rows }, function skeletonRow(_value, index): ReactNode {
        return <Skeleton className={cn("h-3", widths[index % widths.length])} key={index} />;
      })}
    </div>
  );
}

export interface EmptyStateProps {
  readonly title: string;
  /** What the reader can do about it — name the next action where one exists. */
  readonly detail?: string;
  /** The control that takes that next action. */
  readonly action?: ReactNode;
  readonly className?: string;
}

export function EmptyState({ title, detail, action, className }: EmptyStateProps): JSX.Element {
  return (
    <div
      className={cn(
        "flex min-h-24 flex-col items-center justify-center gap-1 border border-dashed p-6 text-center",
        className,
      )}
      role="status"
    >
      <p className="text-sm font-medium">{title}</p>
      {detail !== undefined && <p className="text-sm text-muted-foreground">{detail}</p>}
      {action !== undefined && <div className="mt-2">{action}</div>}
    </div>
  );
}

export interface ErrorStateProps {
  readonly error: unknown;
  /**
   * Shown when the caller can retry. A react-query `refetch` can be passed
   * straight in — the return value is ignored — so a retry button costs one
   * prop. Omit it for a failed mutation: re-running a write on the reader's
   * behalf is the page's decision, not this component's.
   */
  readonly onRetry?: () => unknown;
  readonly retrying?: boolean;
  /** Overrides the leading sentence. */
  readonly title?: string;
  readonly className?: string;
}

/**
 * A failure that says what failed and offers the way out. `onRetry` is wired to
 * the query's own `refetch`, so the button does the thing it names rather than
 * reloading the page.
 */
export function ErrorState({
  error,
  onRetry,
  retrying = false,
  title = "Request failed.",
  className,
}: ErrorStateProps): JSX.Element {
  function retry(): void {
    void onRetry?.();
  }
  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-between gap-3 border border-destructive/30 bg-destructive/10 px-3 py-2 text-destructive",
        className,
      )}
      role="alert"
    >
      <p className="min-w-0">
        <span className="font-semibold">{title}</span> {errorMessage(error)}
      </p>
      {onRetry !== undefined && (
        <Button disabled={retrying} onClick={retry} size="sm" variant="outline">
          {retrying ? "Retrying…" : "Try again"}
        </Button>
      )}
    </div>
  );
}

/**
 * Placeholder for a plot whose charting chunk is still arriving. Sized by the
 * caller so the card does not resize when the chart lands.
 */
export function ChartPanelSkeleton({
  label,
  className,
}: {
  readonly label: string;
  readonly className?: string;
}): JSX.Element {
  return (
    <div aria-label={`Loading ${label} chart`} className={cn("w-full", className)} role="status">
      <Skeleton className="h-full w-full" />
    </div>
  );
}

/**
 * Route-level fallback while a page chunk loads. Shaped like a page — title
 * block, then content — so the shell does not jump when the real page arrives.
 */
export function PageSkeleton({ label = "Loading page" }: { readonly label?: string }): JSX.Element {
  return (
    <div aria-label={label} className="grid gap-4" role="status">
      <div className="grid gap-2">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-6 w-56" />
      </div>
      <Skeleton className="h-40 w-full" />
      <Skeleton className="h-24 w-full" />
    </div>
  );
}
